import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Droplets } from 'lucide-react';
import AppShell from '../layouts/AppShell';
import UserProfileButton from '../components/UserProfileButton';
import EmptyState from '../components/EmptyState';
import { getPatientDisplayName } from '../constants/mockPatients';
import { useSession } from '../contexts/SessionContext';
import { useLanguage } from '../contexts/LanguageContext';
import { patientFluidBalancePath } from '../constants/routes';
import { translateWard } from '../i18n/translations';
import {
  buildMergedFluidData,
  calculateFluidBalanceForCalendarDay,
} from '../utils/flowSheetData';
import { formatFluidNetBadge } from '../utils/fluidBalanceBadge';

export default function FluidBalanceListScreen() {
  const navigate = useNavigate();
  const {
    admittedPatients,
    sessionNotes,
    getFlowSheetData,
  } = useSession();
  const { t, language } = useLanguage();

  const rows = useMemo(
    () =>
      (admittedPatients || []).map((patient) => {
        const displayName = getPatientDisplayName(patient, language);
        const flowSheetData = getFlowSheetData(patient.id);
        const merged = buildMergedFluidData(
          sessionNotes,
          patient.id,
          displayName,
          flowSheetData
        );
        const balance = calculateFluidBalanceForCalendarDay(
          merged.fluidInputs,
          merged.fluidOutputs,
          new Date()
        );
        return {
          patient,
          displayName,
          netBalance: balance.net,
        };
      }),
    [admittedPatients, getFlowSheetData, language, sessionNotes]
  );

  return (
    <AppShell>
      <header className="sticky top-0 z-30 border-b border-gray-200 bg-surface px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => navigate(-1)} className="text-primary" aria-label={t('back')}>
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-page-title">{t('fluidBalance')}</h1>
              <p className="text-xs text-gray-500">{t('fluidBalanceListSubtitle')}</p>
            </div>
          </div>
          <UserProfileButton />
        </div>
      </header>

      <div className="space-y-3 px-4 py-4">
        {rows.length === 0 ? (
          <EmptyState icon={Droplets} title={t('noPatientsFound')} />
        ) : (
          rows.map(({ patient, displayName, netBalance }) => {
            const initial = displayName.charAt(0).toUpperCase();
            return (
              <button
                key={patient.id}
                type="button"
                onClick={() => navigate(patientFluidBalancePath(patient.id))}
                className="card-surface flex w-full items-center gap-3 p-3 text-left transition-colors hover:border-primary-ring"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-primary-mid bg-primary-light/50 text-sm font-bold text-primary">
                  {initial}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[15px] font-bold text-gray-900" dir="auto">
                    {displayName}
                  </p>
                  <p className="text-xs text-gray-500">
                    {t('room')} {patient.room}
                    {patient.ward ? ` · ${translateWard(t, patient.ward)}` : ''}
                  </p>
                  <p className="mt-1 text-xs font-medium text-gray-700">
                    {formatFluidNetBadge(t, netBalance)}
                  </p>
                </div>
              </button>
            );
          })
        )}
      </div>
    </AppShell>
  );
}
